import { Router, RouterModule } from '@angular/router';
import { AsyncPipe, NgClass } from '@angular/common';
import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef, input, output, inject } from '@angular/core';
import type { Client, DeletionDetails, TENANTSINTERFACE } from '@fitness-force/models';
import {
  TranslateModule,
  TranslateService,
} from '@ngx-translate/core';
import { AvatarModule } from 'primeng/avatar';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { PopoverModule } from 'primeng/popover';
import { RippleModule } from 'primeng/ripple';
import { StyleClassModule } from 'primeng/styleclass';
import { TableModule } from 'primeng/table';
import { InputTextModule } from 'primeng/inputtext';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { AssignKeyFobComponent, AssignFitpassComponent, BlockClientComponent, ChangePasswordComponent, ChangeSalesRepresentativeComponent, FolderCreateDialogComponent, AddAttachmentDialogComponent, AttachmentsComponent, TransferProspectComponent, DeleteDigitalPassComponent, SyncClientDialogComponent, BlockClientBookingComponent, UnblockClientBookingComponent, DeleteClientComponent, TaskCreateComponent } from '@fitness-force/shared';
import { CreateOpportunityComponent } from '@fitness-force/crm';
import { ProspectFormComponent } from '@fitness-force/prospect-form-lib';
import { Store } from '@ngrx/store';
import { TENANTS_INFO } from '@fitness-force/state';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { DeletionRequestStatus } from '@fitness-force/enum';
import { FeatureFlagService } from '@fitness-force/shared-utils';
import { DrawerModule } from 'primeng/drawer';

@Component({
  selector: 'fitness-force-prospect-list-actions',
  templateUrl: './prospect-list-actions.component.html',
  styleUrls: ['./prospect-list-actions.component.scss'],
  providers: [DialogService  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    ButtonModule,
    RippleModule,
    StyleClassModule,
    PopoverModule,
    SelectModule,
    DialogModule,
    AvatarModule,
    TableModule,
    InputTextModule,
    CreateOpportunityComponent,
    TaskCreateComponent,
    ChangePasswordComponent,
    BlockClientComponent,
    AssignKeyFobComponent,
    AssignFitpassComponent,
    AttachmentsComponent,
    ChangeSalesRepresentativeComponent,
    RouterModule,
    ProspectFormComponent,
    TransferProspectComponent,
    DeleteDigitalPassComponent,
    SyncClientDialogComponent,
    BlockClientBookingComponent,
    UnblockClientBookingComponent,
    DeleteClientComponent,
    DrawerModule,
    TranslateModule, AsyncPipe, NgClass]
})
export class ProspectListActionsComponent implements OnInit, OnDestroy {
  private readonly translate = inject(TranslateService);
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly dialogService = inject(DialogService);
  private readonly cd = inject(ChangeDetectorRef);
  private readonly featureFlagService = inject(FeatureFlagService);

  readonly columnData = input(new BehaviorSubject<any>(null));
  readonly isAllSelected = input(new BehaviorSubject(false));
  readonly action = output<{ action: string; columnData: any; }>();
  readonly selectedRows = input(new BehaviorSubject(new Map()));
  readonly rowIndex = input(0);
  readonly tableAction = input<boolean>(true);
  readonly showDeleteDigitalPass = input<boolean>(false);
  readonly changeEvent = output<boolean>();

  showOppDialog = false;
  showTaskCreate = false;
  clients = new BehaviorSubject<Array<any>>([]);
  clientDetails: any = [];
  clientIds: number[] = [];
  showCreateProspectPopup: Observable<boolean> = of(false);
  showCreateOpportunityPopup: Observable<boolean> | any = false;
  toggleEditProfileDialog$ = new BehaviorSubject<boolean | null>(false);
  toggleChangePasswordDialog$ = new BehaviorSubject<boolean | null>(false);
  toggleBlockClientDialog$ = new BehaviorSubject<boolean | null>(false);
  toggleAssignKeyFobDialog$ = new BehaviorSubject<boolean | null>(false);
  toggleAssignFitpassDialog$ = new BehaviorSubject<boolean | null>(false);
  toggleChangeSaleRepDialog$ = new BehaviorSubject<boolean | null>(false);
  toggleTransferProspectDialog$ = new BehaviorSubject<boolean | null>(false);
  toggleDeleteDigitalKeyFobDialog$ = new BehaviorSubject<boolean | null>(false);
  toggleSyncClientDialog$ = new BehaviorSubject<boolean | null>(false);
  toggleBlockClientBookingDialog$ = new BehaviorSubject<boolean | null>(false);
  toggleUnblockClientBookingDialog$ = new BehaviorSubject<boolean | null>(false);
  toggleDeleteClientDialog$ = new BehaviorSubject<boolean | null>(false);
  selectedClient: any = new Map();
  deleteProfileModal= false;
  mergeProfileModal= false;
  cancelInvoiceModal= false;
  changeBillOwnerModal= false;
  enquirySourceDisable= true;
  hideDropdown= true;
  confirmDeleteProfileModal = false;
  attachmentsModal=false;
  tableData: any;
  allClientData = new BehaviorSubject(null);
  isObservable = false;

  // coldata
  colData!: any;

  tenantId = 0;
  getAccessTenantList$: Observable<any> | any;
  errMsg: Error | any;
  refDialog?: DynamicDialogRef | null;

  showDeleteClientAction = false;
  isDeletionRequestPending = false;

  showMobileActions:boolean = false;
  showAssignFitpassAction = false;

  private readonly destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.clientIds = [];
    
    // Get the BehaviorSubject from the signal input and subscribe to it
    const columnDataSubject = this.columnData();
    if (columnDataSubject) {
      columnDataSubject.subscribe((val: any) => {
        this.colData = val;
        this.selectedClient.set('id', val);
        this.clients.next(this.getSelectedClientData);
        this.clientDetails = this.getSelectedClientData;
        
        // Check if there's a pending deletion request within the last 30 days
        this.isDeletionRequestPending = this.hasPendingDeletionRequest(val);
      });
    }
    
    this.getAccessTenantList$ = this.store.select(TENANTS_INFO);
    this.getAccessTenantList$.subscribe((tenant: TENANTSINTERFACE) => {
      this.tenantId = tenant.selected_tenant.id;
    });

    // Feature flags are user/tenant-scoped decisions — resolve once, not per row.
    // getFlag$() waits for Statsig init before emitting so there is no race condition.
    this.featureFlagService.getFlag$('show_delete_profile_option')
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe((isEnabled) => {
        this.showDeleteClientAction = isEnabled;
      });

    this.featureFlagService.getFlag$('show_clients_external_providers')
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe((isEnabled) => {
        this.showAssignFitpassAction = isEnabled;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  newOpportunity() {
    this.showOppDialog = true;
    return this.showOppDialog;
  }

  HideTaskCreate() {
    this.showTaskCreate = false;
    this.cd.detectChanges();
  }

  hidePopup(isHide: boolean) {
    this.showCreateProspectPopup = of(isHide);
  }

  get getSelectedClientData(): Array<Client> {
    return [...this.selectedClient.values()];
  }

  showOpp() {
    this.showCreateOpportunityPopup = true;
  }

  OpenPopup() {
    this.showCreateProspectPopup = of(true);
  }

  hideOpportunity() {
    this.showCreateOpportunityPopup = false;
    this.changeEvent.emit(true);
    this.action.emit({action:'updated',columnData: this.colData});
  }

  newTask(data: any) {
    if (this.clientIds.length > 0) {
      this.clientIds = [];
    }
    this.clientIds.push(data.id);

    this.showTaskCreate = true;
  }

  toggleEditProfileDialog() {
    this.toggleEditProfileDialog$.next(true);
  }

  closeEditProfile(e: any) {
    this.toggleEditProfileDialog$.next(false);
    this.changeEvent.emit(e);
  }
  
  closeAssignKeyFob(e: boolean) {
    this.toggleAssignKeyFobDialog$.next(false);
    this.changeEvent.emit(e);
    this.action.emit({action:'updated',columnData: this.colData})
  }

  closeAssignFitpass(e: boolean) {
    this.toggleAssignFitpassDialog$.next(false);
    this.changeEvent.emit(e);
    this.action.emit({action:'updated',columnData: this.colData})
  }

  toggleChangePasswordDialog() {
    this.toggleChangePasswordDialog$.next(true);
  }

  closeChangePasswordDialog() {
    this.toggleChangePasswordDialog$.next(false);
    this.changeEvent.emit(true);
  }

  toggleBlockClientDialog() {
    this.toggleBlockClientDialog$.next(true);
  }

  toggleAssignKeyFobDialog() {
    this.toggleAssignKeyFobDialog$.next(true);
  }

  toggleAssignFitpassDialog() {
    this.toggleAssignFitpassDialog$.next(true);
  }

  closeBlockClientDialog(e: any) {
    if (e.close) this.toggleBlockClientDialog$.next(false);
    this.changeEvent.emit(e);
  }

  closeDeleteDigitalPassDialog(e: any) {
    if (e.close) this.toggleDeleteDigitalKeyFobDialog$.next(false);
    this.changeEvent.emit(e);
  }

  toggleChangeSaleRepDialog() {
    this.toggleChangeSaleRepDialog$.next(true);
  }

  closeChangeSaleRepDialog(e: any) {
    this.toggleChangeSaleRepDialog$.next(false);
    this.changeEvent.emit(typeof e === 'boolean' ? e : !!e);
  }

  toggleDeleteDigitalKeyFobDialog() {
    this.toggleDeleteDigitalKeyFobDialog$.next(true);
  }

  navigateToBillinging(clientData: any) {
    this.router.navigate(['/pos/pos-addon-charges', clientData.id]);
  }

  toggleTransferProspectDialog() {
    this.toggleTransferProspectDialog$.next(true);
  }

  closeTransferProspectDialog(e: any) {
    this.toggleTransferProspectDialog$.next(false);
    this.changeEvent.emit(typeof e === 'boolean' ? e : !!e);
  }

  toggleSyncClientDialog() {
    this.toggleSyncClientDialog$.next(true);
  }

  closeSyncClientDialog(e: boolean) {
    this.toggleSyncClientDialog$.next(false);
    if (e) {
      // If sync was successful, emit change event to refresh data
      this.changeEvent.emit(true);
      this.action.emit({action:'updated',columnData: this.colData});
    }
  }

  /**
   * Check if client booking is currently blocked
   * Returns true if online_booking_stopped_until has a date greater than today
   */
  isClientBookingBlocked(clientData: any): boolean {
    if (!clientData?.online_booking_stopped_until) {
      return false;
    }
    const blockDate = new Date(clientData.online_booking_stopped_until);
    const today = new Date();
    return blockDate > today;
  }

  /**
   * Check if there's a pending deletion request within the next 30 days
   * Returns true if:
   * 1. There's at least one deletion request with status = Pending
   * 2. The requested_date is between today and 30 days from today
   */
  hasPendingDeletionRequest(clientData: any): boolean {
    if (!clientData?.deletion || !Array.isArray(clientData.deletion) || clientData.deletion.length === 0) {
      return false;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0); // Set to start of day for accurate comparison
    
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(today.getDate() - 30);
    thirtyDaysFromNow.setHours(23, 59, 59, 999); // Set to end of day

    return clientData.deletion.some((deletion: DeletionDetails) => {
      // Check if status is Pending
      if (deletion.request_status !== DeletionRequestStatus.Pending) {
        return false;
      }

      // Check if requested_date is between today and 30 days from now
      const requestedDate = new Date(deletion.requested_date);
      return requestedDate >= today && requestedDate <= thirtyDaysFromNow;
    });
  }

  toggleBlockClientBookingDialog() {
    this.toggleBlockClientBookingDialog$.next(true);
  }

  closeBlockClientBookingDialog(e: any) {
    this.toggleBlockClientBookingDialog$.next(false);
    this.changeEvent.emit(e);
  }

  toggleUnblockClientBookingDialog() {
    this.toggleUnblockClientBookingDialog$.next(true);
  }

  closeUnblockClientBookingDialog(e: any) {
    if (e.close) this.toggleUnblockClientBookingDialog$.next(false);
    this.changeEvent.emit(e);
  }

  toggleDeleteClientDialog() {
    this.toggleDeleteClientDialog$.next(true);
  }

  closeDeleteClientDialog(e: any) {
    if (e.close) this.toggleDeleteClientDialog$.next(false);
    this.changeEvent.emit(e);
  }

  addFolder(activeFolder: any = null) {
    this.refDialog = this.dialogService.open(FolderCreateDialogComponent, {
      showHeader: false,
      baseZIndex: 10000,
      contentStyle: { 'max-height': '500px', 'min-width': '400px', 'max-width': '600px' },
      data: { activeFolder },
    });
    if (this.refDialog) {
    this.refDialog.onClose.subscribe((folder: any) => {
        if (folder) {
          // this.getChargeList();
        }
      });
    }
  }

  uploadAttachment() {
    this.refDialog = this.dialogService.open(AddAttachmentDialogComponent, {
      showHeader: false,
      baseZIndex: 10000,
      contentStyle: {  'min-width': '400px', 'max-width': '800px' },
      data: { clientId:this.selectedClient.get('id')?.id },
    });
    if (this.refDialog) {
      this.refDialog.onClose.subscribe((folder: any) => {
        if (folder) {
         // this.getChargeList();
        }
      });
    }
  }

  closeAttachmentDialog(event:any){
    if(event)
    this.attachmentsModal=false;
  }

  toggleActions(event: Event, popover: any) {
    if (window.innerWidth <= 991) { // Mobile/Tablet breakpoint
      this.showMobileActions = true;
    } else {
      popover.toggle(event);
    }
  }

  // Safe getters for signal inputs
  get isAllSelected$() {
    try {
      return this.isAllSelected();
    } catch {
      return new BehaviorSubject(false);
    }
  }

  get selectedRows$() {
    try {
      return this.selectedRows();
    } catch {
      return new BehaviorSubject(new Map());
    }
  }

  get rowIndexValue() {
    try {
      return this.rowIndex();
    } catch {
      return 0;
    }
  }

}